import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Snowflake, Lock, Delete } from "lucide-react";
import { saveStaffPin, isStaffLoggedIn } from "@/lib/staffAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StaffLogin = () => {
  const navigate = useNavigate();
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isStaffLoggedIn()) navigate("/staff", { replace: true });
  }, [navigate]);

  const append = (d) => {
    if (pin.length < 6) {
      setError("");
      setPin(pin + d);
    }
  };
  const back = () => setPin(pin.slice(0, -1));
  const clear = () => setPin("");

  const submit = async (entered) => {
    const candidate = entered ?? pin;
    if (!candidate) return;
    setLoading(true);
    try {
      await axios.post(`${API}/staff/login`, { pin: candidate });
      saveStaffPin(candidate);
      navigate("/staff", { replace: true });
    } catch {
      setError("PIN ungültig");
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  // Auto-submit when 4 digits typed
  useEffect(() => {
    if (pin.length === 4) submit(pin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  const Key = ({ d, onClick, children, testId }) => (
    <button
      onClick={onClick || (() => append(d))}
      disabled={loading}
      className="h-16 sm:h-20 rounded-2xl bg-white shadow-sm border border-slate-200 active:bg-slate-100 active:scale-95 transition text-2xl font-semibold text-slate-900 flex items-center justify-center disabled:opacity-50"
      data-testid={testId || `key-${d}`}
    >
      {children ?? d}
    </button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-sky-50 flex flex-col items-center justify-center px-6 py-8">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-sky-500 text-white shadow-lg mb-4">
            <Snowflake className="w-8 h-8" />
          </div>
          <h1
            className="text-2xl font-semibold text-slate-900 mb-1"
            style={{ fontFamily: "Playfair Display" }}
          >
            Mitarbeiter
          </h1>
          <p className="text-slate-500 text-sm flex items-center justify-center gap-1">
            <Lock className="w-3.5 h-3.5" />
            Bitte PIN eingeben
          </p>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-3 mb-6" data-testid="pin-dots">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`w-3.5 h-3.5 rounded-full transition-all ${
                i < pin.length ? "bg-sky-500 scale-110" : "bg-slate-300"
              }`}
            />
          ))}
        </div>

        {error && (
          <p
            className="text-center text-red-600 text-sm mb-4 animate-pulse"
            data-testid="pin-error"
          >
            {error}
          </p>
        )}

        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <Key key={n} d={String(n)} />
          ))}
          <button
            onClick={clear}
            disabled={loading}
            className="h-16 sm:h-20 rounded-2xl text-slate-500 hover:text-red-500 transition disabled:opacity-50 text-sm"
            data-testid="key-clear"
          >
            Reset
          </button>
          <Key d="0" />
          <button
            onClick={back}
            disabled={loading}
            className="h-16 sm:h-20 rounded-2xl flex items-center justify-center text-slate-500 hover:text-slate-900 transition disabled:opacity-50"
            data-testid="key-back"
          >
            <Delete className="w-6 h-6" />
          </button>
        </div>

        <p className="text-center text-xs text-slate-400 mt-8">
          Grömitzer Eiszeit · Mitarbeiter-Login
        </p>
      </div>
    </div>
  );
};

export default StaffLogin;
